"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect } from "react";
import { AlertCircle, Loader2 } from "lucide-react";

import { PageHeader } from "@/components/ui/header";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { api } from "@/trpc/client";
import { tRPCErrorToUseFormErrors } from "@/utils/misc";
import {
  forgotPasswordSchema,
  type ForgotPasswordSchema,
} from "@/schemas/auth-forms";

export const ForgotPasswordForm = ({
  email,
  onForgotPasswordSuccess,
}: {
  email?: string;
  onForgotPasswordSuccess?: (email: string) => void;
}) => {
  const forgotPasswordForm = useForm<ForgotPasswordSchema>({
    defaultValues: { email },
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onForgotPasswordSubmit = useCallback(
    async (values: ForgotPasswordSchema) => {
      try {
        const result = await api.auth.forgotPassword.mutate(values);
        if (result.success === true) {
          onForgotPasswordSuccess?.(values.email);
        }
      } catch (error) {
        tRPCErrorToUseFormErrors<typeof forgotPasswordForm>(error).forEach(
          (err) => {
            forgotPasswordForm.setError(...err);
          },
        );
      }
    },
    [forgotPasswordForm, onForgotPasswordSuccess],
  );

  useEffect(() => {
    if (email) {
      forgotPasswordForm
        .handleSubmit(onForgotPasswordSubmit)()
        .catch((err) => {
          console.error(err);
        });
    }
  }, [email, forgotPasswordForm, onForgotPasswordSubmit]);

  return (
    <>
      <PageHeader
        heading={"Forgot Password?"}
        text={
          "Enter your email to find your account and a password recovery mail will be sent to the entered email"
        }
      />
      <Form {...forgotPasswordForm}>
        <form
          onSubmit={forgotPasswordForm.handleSubmit(onForgotPasswordSubmit)}
          className="space-y-2"
        >
          <FormField
            control={forgotPasswordForm.control}
            name="email"
            key="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="Enter Your e-mail"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            className="w-full"
            type="submit"
            disabled={forgotPasswordForm.formState.isSubmitting}
          >
            {forgotPasswordForm.formState.isSubmitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Send Reset Code
          </Button>
          {forgotPasswordForm.formState.errors.root?.message && (
            <Alert
              variant="destructive"
              id="root-error-message"
              className="mx-auto"
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              <AlertTitle>
                {forgotPasswordForm.formState.errors.root?.message}
              </AlertTitle>
            </Alert>
          )}
        </form>
      </Form>
    </>
  );
};
